import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MoveToInboxOutlinedIcon from "@mui/icons-material/MoveToInboxOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import SyncAltOutlinedIcon from "@mui/icons-material/SyncAltOutlined";
import { Box, Card, CardContent, Stack, Typography } from "@mui/material";

interface RecentActivityProps {
  data: Array<{ movementType: string; transactions: number; quantity: number }>;
}

const getMovementAppearance = (movementType: string) => {
  const type = movementType.toUpperCase();

  if (type.includes("INWARD") || type.includes("RECEIPT")) {
    return { Icon: MoveToInboxOutlinedIcon, color: "#059669", background: "#ecfdf5" };
  }

  if (type.includes("ISSUE")) {
    return { Icon: SendOutlinedIcon, color: "#2563eb", background: "#eff6ff" };
  }

  if (type.includes("RETURN")) {
    return { Icon: ReplayOutlinedIcon, color: "#0284c7", background: "#f0f9ff" };
  }

  if (type.includes("TRANSFER") || type.includes("SCRAP")) {
    return { Icon: SyncAltOutlinedIcon, color: "#7c3aed", background: "#f5f3ff" };
  }

  return { Icon: ArchiveOutlinedIcon, color: "#475569", background: "#f8fafc" };
};

const RecentActivity = ({ data }: RecentActivityProps) => {
  // Historical adjustment movements remain in the database, but are not a product feature.
  const visibleActivities = data.filter(
    (activity) => activity.movementType.toUpperCase() !== "ADJUSTMENT"
  );

  return (
  <Card
    elevation={0}
    sx={(theme) => ({
      width: "100%",
      minWidth: 0,
      borderRadius: 2,
      border: "1px solid",
      borderColor: theme.palette.mode === "dark" ? "#2a3953" : "divider",
      bgcolor: theme.palette.mode === "dark" ? "#111b2d" : "background.paper",
      overflow: "hidden",
    })}
  >
    <CardContent sx={{ p: { xs: 1.5, md: 1.75 }, "&:last-child": { pb: { xs: 1.5, md: 1.75 } } }}>
      <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
        <Box
          sx={{
            width: 32,
            height: 32,
            display: "grid",
            placeItems: "center",
            borderRadius: 1.5,
            bgcolor: "primary.light",
            color: "primary.dark",
            flex: "0 0 auto",
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 18 }} />
        </Box>
        <Box minWidth={0}>
          <Typography variant="subtitle2" fontWeight={800} lineHeight={1.2}>
            Movement Summary
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Inventory movement totals
          </Typography>
        </Box>
      </Stack>

      {visibleActivities.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No movement activity available yet.
        </Typography>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", lg: "repeat(4, minmax(0, 1fr))" },
            gap: 1,
            width: "100%",
            minWidth: 0,
          }}
        >
          {visibleActivities.map((activity) => {
            const { Icon, color, background } = getMovementAppearance(activity.movementType);

            return (
              <Box
                key={activity.movementType}
                sx={{
                  minWidth: 0,
                  minHeight: 82,
                  p: 1.1,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1.5,
                  bgcolor: (theme) => theme.palette.mode === "dark" ? "#17243a" : "background.default",
                }}
              >
                <Stack direction="row" spacing={0.8} alignItems="center" mb={0.75} minWidth={0}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: 1,
                      color,
                      bgcolor: background,
                      flex: "0 0 auto",
                    }}
                  >
                    <Icon sx={{ fontSize: 16 }} />
                  </Box>
                  <Typography variant="caption" fontWeight={800} noWrap sx={{ textTransform: "uppercase", minWidth: 0 }}>
                    {activity.movementType}
                  </Typography>
                </Stack>
                <Typography variant="h6" fontWeight={850} lineHeight={1.1}>
                  {activity.transactions.toLocaleString()}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.45 }}>
                  {activity.quantity.toLocaleString()} units moved
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </CardContent>
  </Card>
  );
};

export default RecentActivity;
